import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { workspacesApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import ViewEditor from "./ViewEditor";
import ViewPeopleDialog from "./ViewPeopleDialog";
// Retired 28 Sep 2026 (product owner: "remove this message as I was only
// testing, nothing to lose, let's have new concept"). The panel announced a
// carry-over from the group model and then, in the same breath, that there
// was nothing to carry over. The Views tab is about views; it says nothing
// about groups in any state. services/viewGrants.js and
// GET/POST /api/workspaces/:id/carry-over stay, working and tested, for a
// workspace that ever needs one run deliberately - see CLAUDE.md, "Retired,
// still in the code".
// import CarryOverPanel from "./CarryOverPanel";
import { ConfirmDialog, EmptyState, ListSkeleton, Refusal, Surface, dialogPaper, menuPaper, rowSx } from "./WorkspaceUi";
import {
  BUILTIN_VIEW_ID,
  BUILTIN_VIEW_NOTE,
  degradedStartNote,
  formatDate,
  isBuiltInView,
  staleChipText,
  usedByText,
  viewDatabaseText,
  viewFileName,
  viewFileText,
  viewGrants,
  viewStale,
} from "./workspaceFormat";

export const VIEW_RULE =
  "A view belongs to one database and decides what the people who hold it see there: which object types, and which details of each.";

const EMPTY = "__empty__";

// The file is for the technical case only - support, or moving a view between
// instances. Nothing in the everyday path mentions it.
const ADVANCED_NOTE = "A view can be saved as a file and put back — useful for support.";

// Hands the view file to the browser as <name>.view.json.
export const downloadViewFile = (name, objects) => {
  const blob = new Blob([viewFileText(name, objects)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = viewFileName(name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// A file's text, read in the browser.
const readText = (file) =>
  typeof file.text === "function"
    ? file.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });

// A name dialog shared by New view, Copy and Rename.
function NameDialog({ open, title, intro, name, setName, extra, confirmLabel, busy, error, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {intro && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intro}
          </Typography>
        )}
        <Stack spacing={2} sx={{ pt: intro ? 0 : 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="View name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && onConfirm()}
          />
          {extra}
        </Stack>
        {error && <Refusal sx={{ mt: 2 }}>{error}</Refusal>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={busy} disabled={!name.trim()} onClick={onConfirm}>
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// The workspace's views, and who holds each. This is where access is given
// (product owner, 28 Sep 2026): a view belongs to exactly one database, and
// giving someone a view gives them that database - no group in between. The
// Groups tab has gone; what it used to do happens here.
export default function ViewsTab({ workspace, mayChange, notify, onChanged, startNewFor, onStartNewHandled }) {
  const databases = workspace.databases || [];
  const [views, setViews] = useState(null);
  const [archived, setArchived] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // { id, database } | null - the view's own database, so the editor opens on it
  const [menu, setMenu] = useState(null); // { anchor, view }
  const [confirm, setConfirm] = useState(null);
  const [restoring, setRestoring] = useState(null);
  // Who holds one view: the control that gives and takes access.
  const [people, setPeople] = useState(null);
  // One name dialog at a time: { kind: "new" | "rename" | "copyAll", view? }
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState("");
  const [from, setFrom] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");
  // Whether the "from" database's catalogue was read in full, so New view and
  // Start from everything can say when some object types will arrive bare.
  const [fromCounts, setFromCounts] = useState("");
  // Which database the "new object types" chips are measured against.
  const [against, setAgainst] = useState(databases[0]?.name || "");
  // New view: begin with nothing listed rather than the database's types.
  const [startEmpty, setStartEmpty] = useState(false);
  const [stale, setStale] = useState({}); // { viewId: { newTypes, missingTypes } }
  const databaseLabel = (name) => databases.find((d) => d.name === name)?.label || name || "the database";
  // The database the quiet link to Display points at: the one being compared
  // against, else the workspace's first.
  const displayDatabase = against || databases[0]?.name || "";
  const fileInput = useRef(null);
  const replacing = useRef(null);

  const load = useCallback(async () => {
    // Each view says how many people hold it, so nothing else has to be read
    // for the list.
    const [active, old] = await Promise.all([workspacesApi.views(workspace.id), workspacesApi.views(workspace.id, true)]);
    if (active.success) {
      setViews(active.content || []);
      setError("");
    } else setError(active.msg || "Couldn't load the views.");
    setArchived(old?.success ? old.content || [] : []);
    return active.success ? active.content || [] : null;
  }, [workspace.id]);

  useEffect(() => {
    load();
  }, [load]);

  // The Databases tab can send an admin here to make a view for one database
  // ("No view yet, so only workspace admins can open it"). It arrives as a
  // database name; the dialog opens on it, and the request is cleared so it
  // can't fire again on the next render.
  useEffect(() => {
    if (!startNewFor || !mayChange || databases.length === 0) return;
    openNew(startNewFor);
    if (onStartNewHandled) onStartNewHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startNewFor, mayChange, databases.length]);

  // How far each view is behind the chosen database, in one call. Where an
  // older server doesn't answer that, the screen works it out itself from the
  // database's object types and each view's own list.
  useEffect(() => {
    if (!views || !against || views.length === 0) return undefined;
    let live = true;
    (async () => {
      const res = await workspacesApi.viewsStale(workspace.id, against);
      if (!live) return;
      if (res.success) {
        setStale(Object.fromEntries((res.content?.views || []).map((view) => [view.id, { newTypes: view.newTypes, missingTypes: view.missingTypes }])));
        return;
      }
      const catalogue = await workspacesApi.viewCatalogue(workspace.id, against);
      if (!live || !catalogue.success) return;
      const types = catalogue.content?.types || [];
      const stored = views.filter((view) => !isBuiltInView(view));
      const read = await Promise.all(stored.map((view) => workspacesApi.view(workspace.id, view.id)));
      if (!live) return;
      const worked = {};
      read.forEach((answer, index) => {
        if (answer.success) worked[stored[index].id] = viewStale(answer.content?.definition?.objects || [], types);
      });
      setStale(worked);
    })();
    return () => {
      live = false;
    };
  }, [views, against, workspace.id]);

  // New view and Start from everything both read the chosen database's
  // catalogue to build the view - if that read was itself degraded, the
  // dialog says so, since some of the types it lists will arrive bare.
  useEffect(() => {
    const wantsDatabase = (dialog?.kind === "new" && !startEmpty) || dialog?.kind === "copyAll";
    if (!wantsDatabase || from === EMPTY) {
      setFromCounts("");
      return undefined;
    }
    let live = true;
    setFromCounts("");
    (async () => {
      const res = await workspacesApi.viewCatalogue(workspace.id, from);
      if (live && res.success) setFromCounts(res.content?.counts || "");
    })();
    return () => {
      live = false;
    };
  }, [dialog, from, startEmpty, workspace.id]);

  const done = async (msg) => {
    notify(msg);
    const list = await load();
    if (onChanged) onChanged();
    return list;
  };

  const openDialog = (kind, extra = {}) => {
    setDialogError("");
    setDialog({ kind, ...extra });
  };

  // `database` names the one to make it for - the Databases tab sends an
  // admin here with it already chosen, so "add a database, now give people a
  // view of it" is one movement rather than two screens.
  const openNew = (database) => {
    if (databases.length === 0) {
      notify({ text: "This workspace has no databases yet, and a view belongs to one. Add a database first.", tone: "error" });
      return;
    }
    setName("");
    // A view is always for a database; "Start empty" only decides whether it
    // begins with that database's object types listed.
    const wanted = databases.find((d) => d.name === database);
    setFrom((wanted || databases[0]).name);
    setStartEmpty(false);
    openDialog("new");
  };

  // "Start from everything": an editable view holding a database's object types.
  const openCopyAll = (view) => {
    if (databases.length === 0) {
      notify({ text: "This workspace has no databases yet, so there is nothing to start from.", tone: "error" });
      return;
    }
    setName("");
    setFrom(databases[0].name);
    openDialog("copyAll", { view });
  };

  // "All objects in <database>": the library entry made usable for one
  // database, as an ordinary view people can be given. No catalogue is read -
  // it means whatever that database holds, on every request.
  const openUseEverything = () => {
    if (databases.length === 0) {
      notify({ text: "This workspace has no databases yet, and a view belongs to one. Add a database first.", tone: "error" });
      return;
    }
    setName("");
    setFrom(databases[0].name);
    openDialog("useEverything");
  };

  const pickFile = (view) => {
    replacing.current = view;
    if (fileInput.current) {
      fileInput.current.value = "";
      fileInput.current.click();
    }
  };

  const fileChosen = async (event) => {
    const file = event.target.files && event.target.files[0];
    const view = replacing.current;
    if (!file || !view) return;
    let text = "";
    try {
      text = await readText(file);
    } catch (err) {
      // A file that can't be read at all is still sent: the server's answer is
      // the one to show, in its own words.
    }
    setConfirm({
      title: `Replace ${view.name} from ${file.name}?`,
      body: "What this view shows is replaced by the file's object types. Nothing in the database changes, and everyone holding it keeps it.",
      confirmLabel: "Replace",
      tone: "primary",
      run: async () => {
        const res = await workspacesApi.saveView(workspace.id, view.id, { definition: text });
        if (res.success) await done(res.msg || `View ${view.name} replaced.`);
        return res;
      },
    });
  };

  const confirmDialog = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setDialogError("");
    let res;
    if (dialog.kind === "useEverything") {
      res = await workspacesApi.useEverything(workspace.id, from, name.trim());
    } else if (dialog.kind === "copyAll") {
      // A view of your own holding everything the named database has today.
      res = await workspacesApi.copyView(workspace.id, dialog.view.id, name.trim(), from);
    } else if (dialog.kind === "new") {
      res = await workspacesApi.createView(
        workspace.id,
        startEmpty
          ? { name: name.trim(), database: from, definition: { view: name.trim(), objects: [] } }
          : { name: name.trim(), database: from, fromDatabase: from }
      );
    } else {
      res = await workspacesApi.saveView(workspace.id, dialog.view.id, { name: name.trim() });
    }
    setBusy(false);
    if (!res.success) {
      setDialogError(res.msg || "That didn't work. Try again.");
      return;
    }
    const kind = dialog.kind;
    setDialog(null);
    const saved = res.content;
    await done(res.msg || "Saved.");
    // A new view goes straight to choosing its object types; one that means
    // "everything" has nothing to choose, so it goes straight to its people.
    if (kind === "useEverything" && saved?.id) setPeople(saved);
    else if (kind !== "rename" && saved?.id) setEditing({ id: saved.id, database: saved.database || null });
  };

  const copy = async (view) => {
    const res = await workspacesApi.copyView(workspace.id, view.id);
    if (res.success) await done(res.msg || `View ${view.name} copied.`);
    else notify({ text: res.msg || "Couldn't copy the view.", tone: "error" });
  };

  // The file always shows the real thing, never an empty shell. "All objects"
  // has no stored file - it is resolved against a database on every request -
  // so it is downloaded as what it means for the database chosen above: the
  // same list "Start from everything" would copy. A stored view that lists
  // nothing yet gets that list too, and is told so, so the admin can see the
  // shape, edit it and put it back rather than opening `"objects": []`.
  const download = async (view) => {
    const builtIn = isBuiltInView(view);
    if (!against && (builtIn || view.objectCount === 0)) {
      notify({ text: "Choose a database above first: the file lists that database's object types.", tone: "error" });
      return;
    }
    const res = await workspacesApi.view(workspace.id, view.id, against || undefined);
    if (!res.success) {
      notify({ text: res.msg || "Couldn't save the view as a file.", tone: "error" });
      return;
    }
    let objects = res.content.definition?.objects || [];
    let note = "";
    if (objects.length === 0 && !builtIn) {
      const all = await workspacesApi.view(workspace.id, BUILTIN_VIEW_ID, against);
      if (!all.success) {
        notify({ text: all.msg || "Couldn't save the view as a file.", tone: "error" });
        return;
      }
      objects = all.content.definition?.objects || [];
      note = `${view.name} lists no object types yet, so the file shows ${databaseLabel(against)}'s as a starting point. Edit it and put it back with “Replace from file”.`;
    }
    if (objects.length === 0) {
      notify({ text: `Couldn't read ${databaseLabel(against)}'s object types just now, so the file would be empty. Try again shortly.`, tone: "error" });
      return;
    }
    downloadViewFile(res.content.name, objects);
    if (note) notify({ text: note });
  };

  const archive = (view) =>
    setConfirm({
      title: `Archive the view ${view.name}?`,
      body: view.usedBy?.length
        ? `${usedByText(view)}. They stop reaching ${view.databaseLabel || "its database"} until it is restored or they are given another view. You can restore it.`
        : "Nobody holds it. You can restore it.",
      run: async () => {
        const res = await workspacesApi.archiveView(workspace.id, view.id);
        if (res.success) await done(res.msg || `View ${view.name} archived.`);
        return res;
      },
    });

  const restore = async (view) => {
    setRestoring(view.id);
    const res = await workspacesApi.restoreView(workspace.id, view.id);
    setRestoring(null);
    if (res.success) await done(res.msg || `View ${view.name} restored.`);
    else notify({ text: res.msg || "Couldn't restore the view.", tone: "error" });
  };

  if (!views && !error) return <ListSkeleton rows={3} height={72} />;

  const actions = mayChange ? (
    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openNew()}>
      New view
    </Button>
  ) : null;

  const menuView = menu?.view;
  const menuBuiltIn = isBuiltInView(menuView);
  const runMenu = (fn) => () => {
    const view = menuView;
    setMenu(null);
    fn(view);
  };

  return (
    <Stack spacing={2.5}>
      {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}

      <input ref={fileInput} type="file" accept=".json,application/json" hidden onChange={fileChosen} data-testid="view-file" />

      {displayDatabase && (
        <Typography variant="body2" color="text.secondary">
          <Link href={`/admin/display?database=${encodeURIComponent(displayDatabase)}`} underline="hover" sx={{ fontWeight: 600, color: brand.ink }}>
            How {databaseLabel(displayDatabase)} looks
          </Link>{" "}
          — names, colours and fields — is a different decision, and applies to everyone who reads it.
        </Typography>
      )}

      {/* <CarryOverPanel workspace={workspace} mayRun={mayChange} notify={notify} onChanged={load} /> */}

      {(views || []).length > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          // The sentence keeps a readable width and the controls wrap under
          // it: with a database picker beside them, `flex: 1` alone let the
          // text shrink to one word per line.
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 22rem", minWidth: "16rem" }}>
            {VIEW_RULE} Give it to people with “Who sees this”.
          </Typography>
          {databases.length > 1 && (
            <TextField
              select
              size="small"
              label="Compare with"
              value={against}
              onChange={(e) => setAgainst(e.target.value)}
              sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { bgcolor: "#fff" } }}
            >
              {databases.map((d) => (
                <MenuItem key={d.name} value={d.name}>
                  {d.label || d.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {actions}
        </Stack>
      )}

      {views && (
        <Surface>
          {views.length === 0 ? (
            <EmptyState icon={<TuneRoundedIcon />} title="No views yet" action={actions}>
              {VIEW_RULE} Start from a database and trim what people don't need.
            </EmptyState>
          ) : (
            views.map((view, index) => {
              const builtIn = isBuiltInView(view);
              const chip = builtIn ? "" : staleChipText(stale[view.id]);
              return (
                <Stack
                  key={view.id}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  sx={{
                    ...rowSx(!index),
                    cursor: "pointer",
                    "&:hover": { bgcolor: brand.shell },
                    "&:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: -2 },
                  }}
                  onClick={() => setEditing({ id: view.id, database: view.database || null })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setEditing({ id: view.id, database: view.database || null });
                    }
                  }}
                  aria-label={`Open view ${view.name}`}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: brand.canvas, color: brand.ink }}>
                      <TuneRoundedIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{view.name}</Typography>
                        {builtIn && (
                          <Chip size="small" label="Built-in" sx={{ height: 20, fontWeight: 600, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }} />
                        )}
                        {!builtIn && view.databaseLabel && (
                          <Chip
                            size="small"
                            label={view.databaseLabel}
                            sx={{ height: 20, fontWeight: 600, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }}
                          />
                        )}
                        {!builtIn && !viewGrants(view) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={view.database ? "Grants nothing" : "No database yet"}
                            sx={{ height: 20, fontWeight: 600, color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
                          />
                        )}
                        {chip && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={chip}
                            sx={{ height: 20, fontWeight: 600, color: notice.wait.fg, bgcolor: notice.wait.bg, borderColor: notice.wait.line }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {builtIn
                          ? BUILTIN_VIEW_NOTE
                          : `${view.everything ? "Every object type" : `${view.objectCount} ${view.objectCount === 1 ? "object type" : "object types"}`} · ${usedByText(
                              view
                            )}`}
                      </Typography>
                      {!builtIn && !viewGrants(view) && (
                        <Typography variant="caption" sx={{ display: "block", color: notice.wait.fg }}>
                          {viewDatabaseText(view)}
                        </Typography>
                      )}
                      {builtIn && (
                        <Typography variant="caption" color="text.secondary">
                          Copy it, or use it for one database, to give it to people.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}>
                    {!builtIn && formatDate(view.updatedAt) && (
                      <Typography variant="caption" color="text.secondary">
                        Changed {formatDate(view.updatedAt)}
                      </Typography>
                    )}
                    {!builtIn && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPeople(view);
                        }}
                        aria-label={`Who sees ${view.name}`}
                      >
                        Who sees this
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing({ id: view.id, database: view.database || null });
                      }}
                      aria-label={`${mayChange && !builtIn ? "Edit" : "Open"} view ${view.name}`}
                    >
                      {mayChange && !builtIn ? "Edit" : "Open"}
                    </Button>
                    <Tooltip title="More">
                      <IconButton
                        size="small"
                        aria-label={`More for view ${view.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenu({ anchor: e.currentTarget, view });
                        }}
                      >
                        <MoreHorizRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })
          )}
        </Surface>
      )}

      {archived.length > 0 && (
        <Box>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Archived views</Typography>
          <Surface>
            {archived.map((view, index) => (
              <Stack key={view.id} direction="row" spacing={1.5} alignItems="center" sx={rowSx(!index)}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: "text.secondary", fontWeight: 600, overflowWrap: "anywhere" }}>{view.name}</Typography>
                  {formatDate(view.archivedAt) && (
                    <Typography variant="caption" color="text.secondary">
                      Archived {formatDate(view.archivedAt)}
                    </Typography>
                  )}
                </Box>
                {mayChange && (
                  <LoadingButton
                    size="small"
                    loading={restoring === view.id}
                    startIcon={<RestoreRoundedIcon />}
                    onClick={() => restore(view)}
                    aria-label={`Restore view ${view.name}`}
                  >
                    Restore
                  </LoadingButton>
                )}
              </Stack>
            ))}
          </Surface>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)} {...menuPaper}>
        {mayChange && !menuBuiltIn && (
          <MenuItem
            onClick={runMenu((view) => {
              setName(view.name);
              openDialog("rename", { view });
            })}
          >
            Rename
          </MenuItem>
        )}
        {mayChange && menuBuiltIn && <MenuItem onClick={runMenu(openUseEverything)}>Use for a database…</MenuItem>}
        {mayChange && <MenuItem onClick={runMenu(menuBuiltIn ? openCopyAll : copy)}>{menuBuiltIn ? "Start from everything" : "Copy"}</MenuItem>}
        {mayChange && !menuBuiltIn && <MenuItem onClick={runMenu(archive)}>Archive</MenuItem>}
        <Divider />
        <ListSubheader sx={{ lineHeight: 2, fontWeight: 700, bgcolor: "transparent" }}>Advanced</ListSubheader>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, pb: 1, maxWidth: 260 }}>
          {ADVANCED_NOTE}
        </Typography>
        <MenuItem onClick={runMenu(download)}>Download as file</MenuItem>
        {mayChange && !menuBuiltIn && <MenuItem onClick={runMenu(pickFile)}>Replace from file…</MenuItem>}
      </Menu>

      <NameDialog
        open={Boolean(dialog)}
        title={
          dialog?.kind === "rename"
            ? "Rename view"
            : dialog?.kind === "copyAll"
            ? "Start from everything"
            : dialog?.kind === "useEverything"
            ? "All objects, for one database"
            : "New view"
        }
        intro={
          dialog?.kind === "rename"
            ? ""
            : dialog?.kind === "copyAll"
            ? "A view of your own, holding every object type in the database. Trim it afterwards."
            : dialog?.kind === "useEverything"
            ? "Everything that database holds, kept in step with it on its own. Give it to whoever should see all of it."
            : VIEW_RULE
        }
        name={name}
        setName={setName}
        busy={busy}
        error={dialogError}
        confirmLabel={dialog?.kind === "rename" ? "Rename" : "Create"}
        onConfirm={confirmDialog}
        onClose={() => setDialog(null)}
        extra={
          dialog?.kind === "new" || dialog?.kind === "copyAll" || dialog?.kind === "useEverything" ? (
            <>
              <TextField
                select
                fullWidth
                label="Database"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                helperText={[
                  "A view belongs to exactly one database, and gives it to whoever holds the view.",
                  dialog?.kind === "copyAll" ? degradedStartNote(fromCounts) : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {databases.map((d) => (
                  <MenuItem key={d.name} value={d.name}>
                    {d.label || d.name}
                  </MenuItem>
                ))}
              </TextField>
              {dialog?.kind === "new" && (
                <TextField
                  select
                  fullWidth
                  label="Start from"
                  value={startEmpty ? EMPTY : "types"}
                  onChange={(e) => setStartEmpty(e.target.value === EMPTY)}
                  helperText={
                    startEmpty
                      ? "No object types yet. You choose them next."
                      : ["Everything in it, with each object type's name and description. Trim it next.", degradedStartNote(fromCounts)]
                          .filter(Boolean)
                          .join(" ")
                  }
                >
                  <MenuItem value="types">Everything in it</MenuItem>
                  <MenuItem value={EMPTY}>Nothing yet</MenuItem>
                </TextField>
              )}
            </>
          ) : null
        }
      />

      {editing && (
        <ViewEditor
          open
          workspace={workspace}
          viewId={editing.id}
          initialDatabase={editing.database}
          mayChange={mayChange}
          onCopy={(view) => {
            setEditing(null);
            openCopyAll(view);
          }}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            await done(msg);
          }}
        />
      )}

      <ViewPeopleDialog
        open={Boolean(people)}
        workspace={workspace}
        view={people}
        mayChange={mayChange}
        onClose={() => setPeople(null)}
        onSaved={done}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone || "error"}
        onConfirm={() => confirm.run()}
        onClose={() => setConfirm(null)}
      />
    </Stack>
  );
}
